import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { GET_STANDARD_LABELS } from '../graphql/standardLabels';

export interface StandardLabel {
  id: string;
  name: string;
  value: string;
  /** Content type for this label (e.g., 'Text', 'Image') */
  contentType?: string;
}

interface StandardLabelsResponse {
  standardLabels: {
    labels: Array<{
      id: string;
      labelName: string;
      labelShortName: string;
      policy: string;
      contentType?: string;
      scope: {
        global: boolean;
        communityGroups: string[];
        communities: string[];
      };
      labelStatus: string;
      labelNumMap: number;
      createdBy: string;
      createdAt: string;
      lastModifiedBy: string;
      lastModifiedAt: string;
      backupVersions: string[];
    }>;
  };
}

/**
 * Hook to get standard labels for content filtering from the Labels service
 */
export function useStandardLabels() {
  const { data, loading, error, refetch } = useQuery<StandardLabelsResponse>(GET_STANDARD_LABELS, {
    fetchPolicy: 'cache-first',
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const standardLabels: StandardLabel[] = useMemo(() => {
    const serviceLabels = data?.standardLabels?.labels || [];

    // Map service labels
    const mappedLabels = serviceLabels.map(label => ({
      id: label.id,
      name: label.labelName,
      value: label.labelShortName || label.labelName.toLowerCase().replace(/\s+/g, '_'),
      contentType: label.contentType,
    }));

    // Add the "Safe" label that isn't returned from the service
    const safeLabel: StandardLabel = {
      id: 'safe',
      name: 'Safe',
      value: 'safe',
    };

    // Deduplicate by label name — the service may return the same label for
    // different content types (e.g., "Hate Speech" for Text and Image).
    // The dropdown only needs one entry per label name.
    const seen = new Set<string>();
    const uniqueLabels: StandardLabel[] = [];

    for (const label of [safeLabel, ...mappedLabels]) {
      const key = label.name.toLowerCase();

      if (!seen.has(key)) {
        seen.add(key);
        uniqueLabels.push(label);
      }
    }

    return uniqueLabels;
  }, [data]);

  /** Map of label ID or lowercase label name → content type (e.g., 'Text', 'Image') for enriching classification results */
  const labelContentTypeMap: Map<string, string> = useMemo(() => {
    const map = new Map<string, string>();
    const serviceLabels = data?.standardLabels?.labels || [];

    // Use all service labels (not deduplicated) so every label ID is indexed
    for (const label of serviceLabels) {
      if (label.contentType) {
        map.set(label.id, label.contentType);
        map.set(label.labelName.toLowerCase(), label.contentType);
      }
    }

    return map;
  }, [data]);

  return {
    standardLabels,
    labelContentTypeMap,
    loading,
    errors,
    refetch,
  };
}

export default useStandardLabels;
